import { DataRecord, Entity } from "../Types";

/**
 * =====================================================
 * Class: RecordMap
 * =====================================================
 *
 * Description:
 * Lightweight wrapper around a DataRecord to provide
 * convenient methods for:
 * - fast key-value access (get/set)
 * - checking existence of keys (has)
 * - lazy conversion to array (toArray) with caching
 * - size calculation
 * - removal of entries
 *
 * Key Features:
 * - Lazy cache for toArray() ensures a single transformation
 *   even if toArray() is called multiple times in a row.
 * - Cache is invalidated on updates or deletions.
 * - Optimized for read-heavy usage (get/toArray) with few mutations.
 *
 * Author: Samano CASTRE
 * Date: March 1, 2026
 */
export class RecordMap<T extends Entity> {

    /** 
     * Optional cache of values to avoid repeated Object.values() calls
     * Populated lazily on first toArray() call or invalidated on updates/removals
     */
    private cache?: T[];

    /**
     * Constructor
     * @param record - The underlying DataRecord storing key-value pairs
     */
    constructor(private record: DataRecord<T>) { }

    /**
     * =====================================================
     * get
     * =====================================================
     *
     * Returns the value associated with a key.
     * @param id - key to lookup
     * @returns value of type T or undefined if key does not exist
     */
    get = (id: string | number): T => {
        return this.record[id];
    }


    /**
     * =====================================================
     * set
     * =====================================================
     *
     * Adds or updates a key-value pair in the record.
     * Invalidate cache if key already exists to ensure
     * toArray() is consistent.
     *
     * @param key - key to set
     * @param value - value to associate with the key
     * @returns the current RecordMap instance for chaining
     */
    set = (key: string | number, value: T): RecordMap<T> => {
        if (this.has(key)) {
            this.cache = undefined;
        }
        this.record[key] = value;
        this.cache?.push(value);
        return this;
    }

    /**
     * =====================================================
     * has
     * =====================================================
     *
     * Checks if a key exists in the record.
     * @param key - key to check
     * @returns true if key exists, false otherwise
     */
    has = (key: string | number): boolean => {
        return key in this.record;
    }

    /**
    * =====================================================
    * size
    * =====================================================
    *
    * Returns the number of entries in the record.
    * Uses cached array length if available for performance.
    * @returns number of entries
    */
    size = (): number => {
        return this.toArray().length;
    }

    /**
     * =====================================================
     * isEmpty
     * =====================================================
     *
     * Determines whether the RecordMap contains any elements.
     *
     * Behavior:
     * - Delegates to the `size()` method.
     * - Does not trigger cache instantiation.
     * - Does not mutate internal state.
     *
     * Notes:
     * - Time complexity depends on `size()` implementation.
     * - Pure convenience helper for improved readability.
     *
     * @returns `true` if the RecordMap contains no elements,
     *          otherwise `false`.
     */
    isEmpty = (): boolean => {
        return this.size() === 0;
    }

    /**
     * =====================================================
     * remove
     * =====================================================
     *
     * Deletes a key from the record.
     * Invalidates the cache to maintain consistency.
     *
     * @param key - key to delete
     * @returns the current RecordMap instance for chaining
     */
    remove = (key: string | number): RecordMap<T> => {
        if (this.has(key)) {
            delete this.record[key];
            this.cache = undefined;
        }

        return this;
    }

    /**
     * =====================================================
     * toArray
     * =====================================================
     *
     * Lazily converts the record to an array of values.
     * Uses cached array if available, otherwise builds it once.
     * Subsequent calls return the same cached array until
     * cache is invalidated.
     *
     * @returns array of values of type T
     */
    toArray = (): T[] => {
        if (this.cache) {
            return this.cache;
        }

        this.cache = Object.values(this.record);
        return this.cache ?? [];
    }

    /**
    * =====================================================
    * map (generic)
    * =====================================================
    *
    * Applies a function to each value in the RecordMap
    * and returns a new RecordMap of type U.
    *
    * @template U - type of the new RecordMap values
    * @param fn - function mapping T -> U
    * @returns RecordMap<U> with transformed values
    */
    map<U extends Entity>(fn: (param: T) => U): RecordMap<U> {
        const data = {} as DataRecord<U>;
        this.toArray().forEach(item => {
            const newItem = fn(item);
            data[newItem.id] = newItem;
        });
        return new RecordMap<U>(data);
    }

    /**
     * =====================================================
     * forEach
     * =====================================================
     *
     * Iterates over each value in the RecordMap and invokes
     * the provided callback with the value, its key, and
     * the RecordMap itself.
     *
     * Behavior:
     * - Lazy initializes the internal cache if not already done.
     * - Does **not** modify the RecordMap.
     * - Preserves the iteration order of the cache.
     *
     * @param callback - function invoked with each value of type T,
     *                   the corresponding key, and the RecordMap itself.
     */
    forEach = (callback: (value: T, key: string | number, map: RecordMap<T>) => void): void => {
        this.toArray().forEach(item => {
            callback(item, item.id, this);
        });
    }

    doWhile = (
        condition: () => boolean,
        callback: (value: T, key: string | number, map: RecordMap<T>) => void
    ): void => {
        const items = this.toArray();
        let i = 0;

        while (i < items.length && condition()) {
            const item = items[i];
            callback(item, item.id, this);
            i++;
        }
    }



    /**
     * =====================================================
     * filter
     * =====================================================
     *
     * Applies a predicate function to each value in the RecordMap
     * and returns a **new RecordMap** containing only the values
     * for which the predicate returns `true`.
     *
     * Notes:
     * - The original RecordMap is **not modified**, consistent with
     *   Array.prototype.filter behavior.
     * - Cache of the original RecordMap is used to avoid repeated
     *   Object.values() calls.
     *
     * @param predicate - function that receives a value of type T
     *                    and returns a boolean indicating whether
     *                    to keep it.
     * @returns A new RecordMap<T> containing only the filtered values
     */
    filter = (predicate: (param: T) => boolean): RecordMap<T> => {
        const data = {} as DataRecord<T>;
        this.toArray().forEach(item => {
            if (predicate(item)) {
                data[item.id] = item;
            }
        });
        return new RecordMap(data);
    }



    /**
     * =====================================================
     * find
     * =====================================================
     *
     * Searches the RecordMap values and returns the first element
     * that satisfies the provided predicate function.
     *
     * Behavior:
     * - If the cache is not yet instantiated, it triggers `toArray()`
     *   to build it (lazy initialization).
     * - Delegates the search logic to JavaScript's native
     *   Array.prototype.find.
     * - Does not modify the RecordMap.
     *
     * @param predicate - Function that receives a value of type T
     *                    and returns a boolean indicating whether
     *                    the element matches the condition.
     *
     * @returns The first matching element of type T,
     *          or `undefined` if no match is found.
     */
    find = (predicate: (param: T) => boolean): T | undefined => {
        return this.toArray().find(predicate);
    }

    /**
     * =====================================================
     * sort
     * =====================================================
     *
     * Sorts the cached array of values in the RecordMap in-place.
     * 
     * Behavior:
     * - If the cache is not yet instantiated, it triggers `toArray()` to build it.
     * - Delegates the sorting logic to JavaScript's native `Array.prototype.sort`.
     * - Sorting only affects the cache order; underlying record keys remain unchanged.
     * - Returns `this` to allow method chaining.
     *
     * @param compareFn - Optional comparison function (a, b) => number,
     *                    following the same signature as Array.prototype.sort.
     * @returns The current RecordMap instance, sorted in-place.
     */
    sort = (compareFn?: ((a: T, b: T) => number) | undefined): RecordMap<T> => {
        this.toArray().sort(compareFn);
        return this;
    }

    /**
 * =====================================================
 * firstById
 * =====================================================
 *
 * Returns the element with the smallest `id` value.
 *
 * Behavior:
 * - If the cache is not yet instantiated, it triggers `toArray()`
 *   to build it (lazy initialization).
 * - Performs a linear scan using `reduce`.
 * - Does NOT mutate the internal cache order.
 *
 * Notes:
 * - Assumes that `id` is numeric and comparable.
 * - Time complexity: O(n).
 * - Does not rely on insertion order.
 *
 * @returns The element with the smallest `id`,
 *          or `undefined` if the collection is empty.
 */
    firstById = (): T | undefined => {

        return this.toArray().reduce(
            (min, current) => current.id < min.id ? current : min
        );
    }

    /**
 * =====================================================
 * lastById
 * =====================================================
 *
 * Returns the element with the largest `id` value.
 *
 * Behavior:
 * - If the cache is not yet instantiated, it triggers `toArray()`
 *   to build it (lazy initialization).
 * - Performs a linear scan using `reduce`.
 * - Does NOT mutate the internal cache order.
 *
 * Notes:
 * - Assumes that `id` is numeric and comparable.
 * - Time complexity: O(n).
 * - Does not rely on insertion order.
 *
 * @returns The element with the largest `id`,
 *          or `undefined` if the collection is empty.
 */
    lastById = (): T | undefined => {
        if (this.isEmpty()) {
            return undefined;
        }
        return this.toArray().reduce((max, current) =>
            current.id > max.id ? current : max
        );
    }

    findMap<U>(extractor: (value: T) => U | undefined): U | undefined {
        const items = this.toArray();
        for (let i = 0; i < items.length; i++) {
            const result = extractor(items[i]);
            if (result !== undefined) {
                return result;
            }
        }
        return undefined;
    }

    flatMapArray<U extends Entity>(fn: (item: T) => U[]): RecordMap<U> {
        const data = {} as DataRecord<U>;

        this.toArray().forEach(item => {
            const innerArray = fn(item) ?? [];
            innerArray.forEach(inner => {
                data[inner.id] = inner;
            });
        });

        return new RecordMap<U>(data);
    }

    flatMap<U extends Entity>(fn: (item: T) => RecordMap<U>): RecordMap<U> {
        const data = {} as DataRecord<U>;

        this.toArray().forEach(item => {
            const innerMap = fn(item);
            innerMap.toArray().forEach(inner => {
                data[inner.id] = inner;
            });
        });

        return new RecordMap<U>(data);
    }
}